import {
  IResolvedLocalMap,
  ITaskTimeline,
  Roles,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";

export type UnpackedAction = {
  role: string;
  createdAt: string;
  producedBy: string;
  companyName: string;
  contractorCompanyName?: string;
  logo?: string;
};

/**
 * The `ctx` parameter carries the show / stand context that timeline
 * rows don't ship; callers (drawer, history modal) know these from page
 * state. Without them the exhibitor-connection lookup degrades to
 * empty-string params and 400s. `ctx.standId` is the stand UUID
 * (`stands.id`), resolved against `exhibitor_user_connections.stand_id`.
 */
type LegacyTimelineAction = ITaskTimeline & {
  showId?: string;
  standId?: string;
  // BE-resolved acting party (name + company + logo). Organiser rows ship
  // it inline so the FE skips the per-row `apiV2.organiser.getOne`.
  actor?: {
    name: string | null;
    companyName: string | null;
    logoUrl: string | null;
    contractorCompanyName?: string | null;
  } | null;
};

type UnpackContext = {
  showId?: string;
  standId?: string;
};

async function unpackAction(
  action: LegacyTimelineAction,
  localMap: IResolvedLocalMap,
  ctx: UnpackContext = {}
): Promise<UnpackedAction> {
  const { role, createdAt } = action;

  if (role === Roles.EMAIL) {
    throw new Error("Email cannot be unpacked");
  }
  if (!role) {
    throw new Error("Action missing role");
  }

  // Prefer caller-supplied context, falling back to any fields the BE
  // projects on the row. `standId` is the stand UUID.
  const showId = ctx.showId || action.showId || "";
  const standId = ctx.standId || action.standId || "";

  // Seed from the row's denormalised `producedBy`. Downstream lookups may
  // overwrite it with a fresher value, but if a lookup returns null
  // (offboarded user, reassigned stand) we keep the snapshot rather than
  // collapsing to an unlabelled entry.
  const result: UnpackedAction = {
    role: localMap[role] ?? role,
    createdAt,
    producedBy: action.producedBy ?? "",
    companyName: "",
  };

  // Prefer the BE-resolved actor: organiser rows carry company + logo
  // inline, so no per-row lookup is needed. Exhibitor rows have no
  // `actor` and fall through to the connection lookup below.
  if (action.actor) {
    return {
      ...result,
      producedBy: action.actor.name || result.producedBy,
      companyName: action.actor.companyName ?? "",
      ...(action.actor.logoUrl ? { logo: action.actor.logoUrl } : {}),
      ...(action.actor.contractorCompanyName
        ? { contractorCompanyName: action.actor.contractorCompanyName }
        : {}),
    };
  }

  if (role === Roles.EXHIBITOR) {
    if (!showId || !standId || !action.exhibitorId) {
      // Not enough context for the connection lookup — still ship the
      // row-level `producedBy` so the UI shows the name, not a bare role.
      return result;
    }
    const exhibitorConnection = await apiV2.exhibitor.userConnection.getOne({
      showId,
      exhibitorUserId: action.exhibitorId as string,
      standId,
      withDeleted: true,
    });

    if (!exhibitorConnection) return result;

    result.companyName =
      exhibitorConnection.exhibitorPair?.companyToShow?.company?.name || "";
    result.logo =
      exhibitorConnection.exhibitorPair?.companyToShow?.company?.companyInfo?.logoFile?.url;
    result.contractorCompanyName = exhibitorConnection.stand?.keyContractor;
    // Only overwrite the snapshot when the live lookup has a name.
    if (exhibitorConnection.exhibitorUser?.name) {
      result.producedBy = exhibitorConnection.exhibitorUser.name;
    }
  }

  return result;
}

export default unpackAction;
